import {Suspense, useState} from 'react';
import {format, parse} from 'date-fns';
import {diApi} from '@di-shared/api/di';
import {RegisterVehicleCommand} from '@di-shared/api/di/__generated';
import {emitEventLog} from '@di-shared/features/analytics';
import Popper from '@di-shared/features/common/Popper';
import useApiError from '@di-shared/features/common/useApiError';
import useCcWebviewUtilsHelper from '@di-shared/features/common/useCcWebviewUtilsHelper';
import {useInferableTranslation} from '@di-shared/features/common/useInferableTranslation';
import DateInput from '@di-shared/features/public-service/vehicle-inspection/DateInput';
import {sanitizeLicensePlateNumber} from '@di-shared/utils/vehicle';
import PreventForm from '@shared/PreventForm';
import {ccWebviewUtils} from '@shared/cci/features/webviewUtils';
import {isValidDate} from '@shared/dateUtils';
import {getQueryKey} from '@shared/queryKeyMap';
import usePendingHandler from '@shared/usePendingHandler';
import {useSuspenseQuery} from '@tanstack/react-query';

type VehicleInspectionInfoEditFormFields = Omit<RegisterVehicleCommand, 'puid' | 'birthday'>;

// yyyy-MM-dd → Date 변환
function stringToDate(str: string | undefined): Date | undefined {
  if (!str) {
    return undefined;
  }
  // 안전하게 파싱 (포맷 보장 일 때)
  return parse(str, 'yyyy-MM-dd', new Date());
}

// Date → yyyy-MM-dd 변환
function dateToString(date: Date | null | undefined): string | undefined {
  if (date === undefined) {
    return undefined;
  }

  if (!date) {
    return '';
  }
  return format(date, 'yyyy-MM-dd');
}

interface InspectionInfoEditProps {
  selectedIndex: number;
  puid: string;
  vin: string;
}

function VehicleInspectionEditVehicle(props: InspectionInfoEditProps) {
  return (
    <Suspense>
      <Inner {...props} />
    </Suspense>
  );
}

function Inner({selectedIndex, puid, vin}: InspectionInfoEditProps) {
  const {t} = useInferableTranslation();
  const {openAlert} = useCcWebviewUtilsHelper();
  const {resolveError} = useApiError();

  /**
   * [2025-10-02:Jeyoung Lee]
   * ‼️ 잠재적인 버그가 있음 ‼️
   * vehicle을 조회하는 id가 따로 없고 puid와 licensePlateNumber의 조합으로 이루어짐
   * 그런데 url에 licensePlateNumber를 노출하는 것이 보안적으로 문제가 된다고 판단
   * Home > 차량 검사예약 컴포넌트에서 넘어오는 해당 페이지는 단순히 statusList의 index를 넘겨주어 수정하게 됨
   * @link https://hmg.dooray.com/task/3524050935366243006/4163941054205841300
   */
  const {data: reservationStatusData} = useSuspenseQuery({
    queryKey: [getQueryKey(diApi.publicServiceInspection.getReservationStatus), puid],
    queryFn: () => diApi.publicServiceInspection.getReservationStatus({puid, vin}),
  });

  const selectedLicensePlateNumber = reservationStatusData.data.statusList[selectedIndex].licensePlateNumber;

  // GET /public-service/inspection/vehicle
  const {data: vehicleData} = useSuspenseQuery({
    queryKey: [getQueryKey(diApi.publicServiceInspection.getVehicle2), puid, selectedLicensePlateNumber],
    queryFn: () =>
      diApi.publicServiceInspection.getVehicle2({
        puid,
        licensePlateNumber: selectedLicensePlateNumber,
      }),
  });

  /**
   * [2026-01-07:Jeyoung Lee]
   * 비어있으면 undefined, invalid면 null
   * fixme: DateInput 에서 바로 string 반환했으면 될것 같은데 우선은 간단하게 수정, 추후 리팩토링
   */

  const [form, setForm] = useState<VehicleInspectionInfoEditFormFields>({
    registrationDate: vehicleData.data.vehicle.registrationDate || undefined,
    lastInspectionDate: vehicleData.data.vehicle.lastInspectionDate || undefined,
    licensePlateNumber: vehicleData.data.vehicle.licensePlateNumber,
  });
  const [isTouchSubmit, setIsTouchSubmit] = useState(false);
  const isRegistrationDateValid = form.registrationDate === undefined || isValidDate(form.registrationDate);
  const isRegistrationDateError = isTouchSubmit && !isRegistrationDateValid;
  const isLastInspectionDateValid = form.lastInspectionDate === undefined || isValidDate(form.lastInspectionDate);
  const isLastInspectionDateError = isTouchSubmit && !isLastInspectionDateValid;

  const isFormValid = isRegistrationDateValid && isLastInspectionDateValid;

  function updateFormField(update: Partial<VehicleInspectionInfoEditFormFields>) {
    const licensePlateNumber = sanitizeLicensePlateNumber(form.licensePlateNumber, update.licensePlateNumber);

    setForm((prev) => ({...prev, ...update, licensePlateNumber}));
  }

  const [submitVehicleInspectionInfoEditForm] = usePendingHandler(
    async () => {
      const requestBody = {
        puid,
        ...form,
      };
      // POST /public-service/inspection/vehicle/update
      await diApi.publicServiceInspection.updateVehicle2({
        requestBody: {
          ...requestBody,
          licensePlateNumber: requestBody.licensePlateNumber.trim(),
        },
      });
      await openAlert({
        title: '',
        message: t('T30493'),
        okText: t('T30494'),
      });
      ccWebviewUtils.closeContent();
    },
    async (error: any) => {
      const [openErrorAlert] = await resolveError(error);
      openErrorAlert();
    }
  );

  return (
    <>
      <main
        id="content"
        className="content"
      >
        <PreventForm onSubmit={() => {}}>
          <section className="section">
            <div className="section_heading type_sub">
              <h2 className="section_heading_title">{t('T30527')}</h2>
              <p className="section_heading_desc">{t('T30504')}</p>
            </div>
            <div className="service_info_area date_area">
              <h3 className="info_title">
                {`${t('T30505')} (${t('T30309')})`}
                <RegistrationDateTooltip />
              </h3>
              <DateInput
                initialDate={stringToDate(form.registrationDate)}
                onChange={(date) => updateFormField({registrationDate: dateToString(date)})}
                isError={isRegistrationDateError}
              />
              {isRegistrationDateError && <p className="txt_error body_small_r">{t('T30506')}</p>}
            </div>
            <div className="service_info_area date_area">
              <h3 className="info_title">
                {`${t('T30507')} (${t('T30309')})`}
                <LastInspectionDateTooltip />
              </h3>
              <DateInput
                initialDate={stringToDate(form.lastInspectionDate)}
                onChange={(date) => updateFormField({lastInspectionDate: dateToString(date)})}
                isError={isLastInspectionDateError}
              />
              {isLastInspectionDateError && <p className="txt_error body_small_r">{t('T30508')}</p>}
            </div>
          </section>
        </PreventForm>
      </main>
      <div className="floating_action_area">
        <button
          type="button"
          className="btn btn_large btn_filled btn_full"
          onClick={() => {
            if (!isFormValid) {
              setIsTouchSubmit(true);
              return;
            }
            emitEventLog({
              event: 'public_service_interaction',
              data_action: 'vehicle inspection click',
              data_name: '서비스 연동하기',
            });
            submitVehicleInspectionInfoEditForm();
          }}
        >
          {t('T30509')}
        </button>
      </div>
    </>
  );
}

export default VehicleInspectionEditVehicle;

function RegistrationDateTooltip() {
  const {t} = useInferableTranslation();

  return (
    <Popper
      placement="bottom"
      className="tooltip"
      marginLeft={1.5}
      tooltip={
        <div
          tabIndex={0}
          role="tooltip"
        >
          <strong className="tooltip_title">{t('T30510')}</strong>
          <ul className="tooltip_list">
            <li className="tooltip_item">{t('T30511')}</li>
            <li className="tooltip_item">{t('T30512')}</li>
          </ul>
        </div>
      }
    >
      <div className="tooltip_area tooltip_area_single">
        <button
          type="button"
          className="btn_tooltip"
          aria-controls="tooltipStatement01"
          aria-expanded="false"
          aria-describedby="tooltipBox01"
        >
          <span
            className="ic ic_information_16px"
            aria-hidden="true"
          ></span>
          <span className="blind">{t('T10044')}</span>
        </button>
      </div>
    </Popper>
  );
}

function LastInspectionDateTooltip() {
  const {t} = useInferableTranslation();

  return (
    <Popper
      placement="bottom"
      className="tooltip"
      marginLeft={1.5}
      tooltip={
        <div
          tabIndex={0}
          role="tooltip"
        >
          <strong className="tooltip_title">{t('T30513')}</strong>
          <ul className="tooltip_list">
            <li className="tooltip_item">{t('T30514')}</li>
            <li className="tooltip_item">{t('T30515')}</li>
          </ul>
        </div>
      }
    >
      <div className="tooltip_area tooltip_area_single">
        <button
          type="button"
          className="btn_tooltip"
          aria-controls="tooltipStatement01"
          aria-expanded="false"
          aria-describedby="tooltipBox02"
        >
          <span
            className="ic ic_information_16px"
            aria-hidden="true"
          ></span>
          <span className="blind">{t('T10044')}</span>
        </button>
      </div>
    </Popper>
  );
}
